import React, { useEffect, useState } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import CcButton from "../../CoilConfigurator/components/CcButton";
import ConfirmDialog from "../../CoilConfigurator/components/ConfirmDialog";
import { configurationService } from "../services/configurationService";
import type { OnPipeAssumptions } from "../models/types";
import { DEFAULT_ON_PIPE_ASSUMPTIONS } from "../domain/defaultAssumptions";
import {
  ASSUMPTION_FIELD_META,
  cloneAssumptions,
  getAssumptionDisplayValue,
  setAssumptionValue,
  type AssumptionFieldMeta,
} from "../domain/assumptionMeta";

const AssumptionsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<OnPipeAssumptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<OnPipeAssumptions>(() =>
    cloneAssumptions(DEFAULT_ON_PIPE_ASSUMPTIONS),
  );
  const [draftValues, setDraftValues] = useState<Record<string, string>>(() => {
    const next: Record<string, string> = {};
    for (const meta of ASSUMPTION_FIELD_META) {
      next[meta.id] = getAssumptionDisplayValue(DEFAULT_ON_PIPE_ASSUMPTIONS, meta);
    }
    return next;
  });

  const syncDraftValues = (assumptions: OnPipeAssumptions) => {
    const next: Record<string, string> = {};
    for (const meta of ASSUMPTION_FIELD_META) {
      next[meta.id] = getAssumptionDisplayValue(assumptions, meta);
    }
    setDraftValues(next);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await configurationService.getAssumptions();
        if (cancelled) return;
        const cloned = cloneAssumptions(data);
        setDraft(cloned);
        syncDraftValues(cloned);
      } catch (err: unknown) {
        if (cancelled) return;
        const fallback = cloneAssumptions(DEFAULT_ON_PIPE_ASSUMPTIONS);
        setDraft(fallback);
        syncDraftValues(fallback);
        setError(err instanceof Error ? err.message : "Failed to load assumptions.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateField = (meta: AssumptionFieldMeta, raw: string) => {
    setDraftValues((prev) => ({ ...prev, [meta.id]: raw }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[meta.id];
      return next;
    });
    setFeedback(null);
    setDraft((prev) => {
      if (meta.kind === "text") return setAssumptionValue(prev, meta, raw);
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return prev;
      return setAssumptionValue(prev, meta, parsed);
    });
  };

  const buildPayload = (): OnPipeAssumptions | null => {
    const nextErrors: Record<string, string> = {};
    let payload = cloneAssumptions(draft);

    for (const meta of ASSUMPTION_FIELD_META) {
      const raw = draftValues[meta.id] ?? "";
      if (meta.kind === "text") {
        const value = raw.trim();
        if (!value) nextErrors[meta.id] = `${meta.label} is required.`;
        else payload = setAssumptionValue(payload, meta, value);
        continue;
      }
      const parsed = Number(raw);
      if (!raw.trim() || !Number.isFinite(parsed) || parsed < 0) {
        nextErrors[meta.id] = `${meta.label} must be a non-negative number.`;
      } else {
        payload = setAssumptionValue(payload, meta, parsed);
      }
    }

    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return null;
    return payload;
  };

  const handleReset = () => {
    const defaults = cloneAssumptions(DEFAULT_ON_PIPE_ASSUMPTIONS);
    setDraft(defaults);
    syncDraftValues(defaults);
    setFieldErrors({});
    setError(null);
    setFeedback("Reset to defaults. Save to persist.");
  };

  const handleSaveClick = () => {
    const payload = buildPayload();
    if (!payload) return;
    setError(null);
    setFeedback(null);
    setPendingPayload(payload);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (saving) return;
    setConfirmOpen(false);
    setPendingPayload(null);
  };

  const confirmSave = async () => {
    if (!pendingPayload || saving) return;
    setSaving(true);
    setError(null);
    setFeedback(null);
    try {
      const saved = await configurationService.updateAssumptions(pendingPayload);
      const cloned = cloneAssumptions(saved);
      setDraft(cloned);
      syncDraftValues(cloned);
      setConfirmOpen(false);
      setPendingPayload(null);
      setFeedback("Assumptions saved. Configurations will be recalculated with the new values.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save assumptions.");
      setConfirmOpen(false);
      setPendingPayload(null);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="op-form-card">
        <p className="op-muted">Loading assumptions…</p>
      </section>
    );
  }

  return (
    <div className="op-assumptions-panel">
      <div className="op-info-note">
        <InfoCircleOutlined aria-hidden />
        <p>
          Constants the selection formulas depend on. Tuning one of these and saving updates the
          engineering assumptions used by Blast Frame and Induction Coil sizing rules.
        </p>
      </div>

      <div className="op-table-scroll op-form-card op-form-card--flush">
        <table className="op-plain-table">
          <thead>
            <tr>
              <th>Assumption</th>
              <th>Value</th>
              <th>Used In</th>
              <th>Why it matters</th>
            </tr>
          </thead>
          <tbody>
            {ASSUMPTION_FIELD_META.map((meta) => (
              <tr key={meta.id}>
                <td>{meta.label}</td>
                <td>
                  <div className="op-assumption-value-cell">
                    <input
                      className="op-assumption-value-cell__input"
                      type={meta.kind === "number" ? "number" : "text"}
                      step={meta.kind === "number" ? "any" : undefined}
                      min={meta.kind === "number" ? "0" : undefined}
                      value={draftValues[meta.id] ?? ""}
                      disabled={saving}
                      aria-label={meta.label}
                      aria-invalid={Boolean(fieldErrors[meta.id])}
                      onChange={(event) => updateField(meta, event.target.value)}
                    />
                    {meta.unit ? (
                      <span className="op-assumption-value-cell__unit">{meta.unit}</span>
                    ) : null}
                  </div>
                  {fieldErrors[meta.id] ? (
                    <p className="op-field-error" role="alert">
                      {fieldErrors[meta.id]}
                    </p>
                  ) : null}
                </td>
                <td>
                  <span
                    className={`op-assumption-used-in${
                      meta.usedIn.includes("Induction Coil")
                        ? " op-assumption-used-in--coil"
                        : ""
                    }`}
                  >
                    {meta.usedIn}
                  </span>
                </td>
                <td className="op-plain-table__desc">{meta.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error ? (
        <p className="op-field-error" role="alert">
          {error}
        </p>
      ) : null}
      {feedback ? <p className="op-muted op-assumptions-panel__save-msg">{feedback}</p> : null}

      <div className="op-assumptions-panel__actions">
        <CcButton variant="secondary" type="button" disabled={saving} onClick={handleReset}>
          Reset to Defaults
        </CcButton>
        <CcButton variant="primary" type="button" disabled={saving} onClick={handleSaveClick}>
          {saving ? "Saving…" : "Save Assumptions"}
        </CcButton>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Recalculate configurations?"
        message="Saving these assumptions will recalculate all configurations using the new values. Do you want to continue?"
        confirmLabel={saving ? "Saving…" : "Save & Recalculate"}
        cancelLabel="Cancel"
        loading={saving}
        onClose={closeConfirm}
        onConfirm={confirmSave}
      />
    </div>
  );
};

export default AssumptionsPanel;
